import {Banner, Button} from '@wandb/components';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
// @todo: Migrate to 'Dialog.Modal' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as Dialog from '@wandb/weave/components/Dialog';
// @todo: Migrate to 'TextField' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {useState} from 'react';

import {useUpdateRun} from '../../state/runs/hooks';
import {setCustomRunName as setCustomRunNameAction} from '../../state/views/customRunNames/actions';
import {type Ref as CustomRunNamesRef} from '../../state/views/customRunNames/types';
import {useMaybeViewAction, usePartMaybe} from '../../state/views/hooks';
import {propagateErrorsContext} from '../../util/errors';
import {getCustomRunName} from '../../util/runs';
import {useIsInLoadedReportContext} from '../ReportPage/LoadedReportContext';

type RenameRunDialogProps = {
  runId: string;
  runName: string;
  runDisplayName: string;
  customRunNamesRef: CustomRunNamesRef | undefined;
  closeDialog: () => void;
  isCrossProjectRun: boolean;
  isOpen: boolean;
};

export const RenameRunDialog = ({
  runId,
  runName,
  runDisplayName,
  customRunNamesRef,
  closeDialog,
  isCrossProjectRun,
  isOpen,
}: RenameRunDialogProps) => {
  const isInReport = useIsInLoadedReportContext();

  const updateRun = useUpdateRun({
    context: propagateErrorsContext(),
  });
  const customRunNames = usePartMaybe(customRunNamesRef);
  const customRunName = getCustomRunName(runName, false, customRunNames) ?? '';
  const setCustomRunName = useMaybeViewAction(
    customRunNamesRef,
    setCustomRunNameAction
  );

  const [newDisplayName, setNewDisplayName] = useState(runDisplayName);
  const [newCustomName, setNewCustomName] = useState(customRunName);

  const onSave = async () => {
    if (newDisplayName !== runDisplayName) {
      try {
        await updateRun(runId, runName, {displayName: newDisplayName});
      } catch (err) {
        toast('Failed to update run name', {type: 'error'});
      }
    }
    if (newCustomName !== customRunName) {
      setCustomRunName(runName, newCustomName);
    }
    closeDialog();
  };

  const onEnterKeyDown = (key: KeyboardEvent['key']) => {
    if (key === 'Enter') {
      onSave();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={closeDialog}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content className="w-[500px] rounded-lg">
          <Dialog.Title>Edit run name</Dialog.Title>
          <Dialog.Description className="text-moon-700 dark:text-moon-300">
            Edit the run name for the entire project, or add a display name for
            this {isInReport ? 'panel grid' : 'workspace'} only.
          </Dialog.Description>
          {isCrossProjectRun && (
            <Banner variant="warning" mt={6} isDismissible={false}>
              <span>
                <b>This run belongs to another project.</b> Renaming it will
                affect the source project and anywhere else it is used.
              </span>
            </Banner>
          )}
          <div className="mt-16 mb-24 flex flex-col gap-12">
            <div className="flex flex-col gap-4">
              <label htmlFor="project-run-name" className="font-semibold">
                Run name
              </label>
              <TextField
                id="project-run-name"
                value={newDisplayName}
                onChange={setNewDisplayName}
                onKeyDown={onEnterKeyDown}
                placeholder="Choose a run name"
                isContainerNightAware
              />
            </div>
            <div className="flex flex-col gap-4">
              <label htmlFor="custom-run-name" className="font-semibold">
                Display name (this {isInReport ? 'panel grid' : 'workspace'})
              </label>
              <TextField
                id="custom-run-name"
                value={newCustomName}
                onChange={setNewCustomName}
                onKeyDown={onEnterKeyDown}
                placeholder={newDisplayName || 'Choose a display name'}
                isContainerNightAware
              />
            </div>
          </div>
          <div className="flex gap-8">
            <Button isDisabled={!newDisplayName} onPress={onSave}>
              Save
            </Button>
            <Button variant="secondary" onPress={closeDialog}>
              Cancel
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
